import React, { memo } from 'react';
import { Box, Skeleton, useTheme } from '@ublend-npm/aulaui-core';
import { getSpaceWidgetColors } from './spaceWidgetColors';
import {
  SPACE_CARD_BORDER_RADIUS,
  SPACE_CARD_BODY_BLOCK_GAP,
  SPACE_CARD_BODY_BLOCK_HEIGHT,
  SPACE_CARD_DESCRIPTION_HEIGHT,
  SPACE_CARD_HEADER_ROW_GAP,
  SPACE_CARD_HEADER_ROW_HEIGHT,
  SPACE_CARD_HEIGHT,
  SPACE_CARD_ICON_BUTTON_SIZE,
  SPACE_CARD_INNER_GAP,
  SPACE_CARD_INNER_HEIGHT,
  SPACE_CARD_INNER_WIDTH,
  SPACE_CARD_PADDING_X,
  SPACE_CARD_PADDING_Y,
  SPACE_CARD_PROGRESS_SECTION_GAP,
  SPACE_CARD_PROGRESS_SECTION_HEIGHT,
  SPACE_CARD_SHADOW,
  SPACE_CARD_SKELETON_SURFACE,
  SPACE_CARD_TEXT_BLOCK_GAP,
  SPACE_CARD_TEXT_BLOCK_HEIGHT,
  SPACE_CARD_TITLE_DESC_BLOCK_HEIGHT,
  SPACE_CARD_TITLE_DESC_GAP,
  SPACE_CARD_TITLE_HEIGHT,
  SPACE_CARD_WIDTH,
  SPACE_META_PILL_HEIGHT,
  SPACE_META_PILL_RADIUS,
  SPACE_META_PILLS_CONTAINER_HEIGHT,
  SPACE_META_PILL_GAP,
  SPACE_PROGRESS_BAR_HEIGHT,
  SPACE_PROGRESS_LABEL_HEIGHT,
  SPACE_RESUME_BUTTON_HEIGHT,
} from './spaceWidgetConstants';

type SpaceCardSkeletonProps = {
  skeletonSurface?: string;
};

const SpaceCardSkeleton = ({
  skeletonSurface = SPACE_CARD_SKELETON_SURFACE,
}: SpaceCardSkeletonProps) => {
  const { palette } = useTheme();
  const colors = getSpaceWidgetColors(palette);

  return (
    <Box
      aria-hidden="true"
      data-testid="space-card-skeleton"
      sx={{
        width: SPACE_CARD_WIDTH,
        minWidth: SPACE_CARD_WIDTH,
        maxWidth: SPACE_CARD_WIDTH,
        height: SPACE_CARD_HEIGHT,
        flexShrink: 0,
        borderRadius: SPACE_CARD_BORDER_RADIUS,
        bgcolor: colors.cardBg,
        boxSizing: 'border-box',
        boxShadow: SPACE_CARD_SHADOW,
        py: SPACE_CARD_PADDING_Y,
        px: SPACE_CARD_PADDING_X,
      }}
    >
      <Box
        sx={{
          width: SPACE_CARD_INNER_WIDTH,
          height: SPACE_CARD_INNER_HEIGHT,
          display: 'flex',
          flexDirection: 'column',
          gap: SPACE_CARD_INNER_GAP,
          boxSizing: 'border-box',
        }}
      >
        <Box
          sx={{
            width: SPACE_CARD_INNER_WIDTH,
            height: SPACE_CARD_BODY_BLOCK_HEIGHT,
            display: 'flex',
            flexDirection: 'column',
            gap: SPACE_CARD_BODY_BLOCK_GAP,
            boxSizing: 'border-box',
          }}
        >
          <Box
            sx={{
              width: SPACE_CARD_INNER_WIDTH,
              height: SPACE_CARD_HEADER_ROW_HEIGHT,
              display: 'flex',
              alignItems: 'center',
              gap: SPACE_CARD_HEADER_ROW_GAP,
              flexShrink: 0,
            }}
          >
            <Skeleton
              kind="circular"
              width={SPACE_CARD_ICON_BUTTON_SIZE}
              height={SPACE_CARD_ICON_BUTTON_SIZE}
              sx={{ bgcolor: skeletonSurface, flexShrink: 0 }}
            />
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'flex-end',
                gap: SPACE_META_PILL_GAP,
                flex: 1,
                minWidth: 0,
                height: SPACE_META_PILLS_CONTAINER_HEIGHT,
              }}
            >
              <Skeleton
                kind="rounded"
                width={53}
                height={SPACE_META_PILL_HEIGHT}
                sx={{
                  borderRadius: SPACE_META_PILL_RADIUS,
                  bgcolor: skeletonSurface,
                }}
              />
              <Skeleton
                kind="rounded"
                width={117}
                height={SPACE_META_PILL_HEIGHT}
                sx={{
                  borderRadius: SPACE_META_PILL_RADIUS,
                  bgcolor: skeletonSurface,
                }}
              />
            </Box>
          </Box>

          <Box
            sx={{
              width: SPACE_CARD_INNER_WIDTH,
              height: SPACE_CARD_TEXT_BLOCK_HEIGHT,
              display: 'flex',
              flexDirection: 'column',
              gap: SPACE_CARD_TEXT_BLOCK_GAP,
              boxSizing: 'border-box',
            }}
          >
            <Box
              sx={{
                width: SPACE_CARD_INNER_WIDTH,
                height: SPACE_CARD_TITLE_DESC_BLOCK_HEIGHT,
                display: 'flex',
                flexDirection: 'column',
                gap: SPACE_CARD_TITLE_DESC_GAP,
                flexShrink: 0,
                boxSizing: 'border-box',
              }}
            >
              <Box
                sx={{
                  width: '100%',
                  height: SPACE_CARD_TITLE_HEIGHT,
                  display: 'flex',
                  flexDirection: 'column',
                  gap: '0.5rem',
                  justifyContent: 'center',
                }}
              >
                <Skeleton
                  kind="rounded"
                  height={16}
                  sx={{
                    width: '100%',
                    borderRadius: '0.375rem',
                    bgcolor: skeletonSurface,
                  }}
                />
                <Skeleton
                  kind="rounded"
                  height={16}
                  sx={{
                    width: '78%',
                    borderRadius: '0.375rem',
                    bgcolor: skeletonSurface,
                  }}
                />
              </Box>

              <Skeleton
                kind="rounded"
                height={SPACE_CARD_DESCRIPTION_HEIGHT}
                sx={{
                  width: '100%',
                  borderRadius: '0.375rem',
                  bgcolor: skeletonSurface,
                }}
              />
            </Box>

            <Box
              sx={{
                width: '100%',
                height: SPACE_CARD_PROGRESS_SECTION_HEIGHT,
                display: 'flex',
                flexDirection: 'column',
                gap: SPACE_CARD_PROGRESS_SECTION_GAP,
                flexShrink: 0,
              }}
            >
              <Skeleton
                kind="rounded"
                height={SPACE_PROGRESS_BAR_HEIGHT}
                sx={{
                  width: '100%',
                  borderRadius: '999px',
                  bgcolor: skeletonSurface,
                }}
              />
              <Skeleton
                kind="rounded"
                height={SPACE_PROGRESS_LABEL_HEIGHT}
                sx={{
                  width: '42%',
                  borderRadius: '0.375rem',
                  bgcolor: skeletonSurface,
                }}
              />
            </Box>
          </Box>
        </Box>

        <Skeleton
          kind="rounded"
          height={SPACE_RESUME_BUTTON_HEIGHT}
          sx={{
            width: SPACE_CARD_INNER_WIDTH,
            borderRadius: '0.5rem',
            bgcolor: skeletonSurface,
            flexShrink: 0,
          }}
        />
      </Box>
    </Box>
  );
};

export default memo(SpaceCardSkeleton);
